export const ESTIMATE_INPUT_LIMITS = {
  weight: { min: 1, max: 60_000, decimals: 1, unit: "g" },
  dimension: { min: 0.1, max: 300, decimals: 1, unit: "cm" },
} as const;

export type EstimateNumericField = "weight" | "length" | "width" | "height";

export type EstimateInputs = Record<EstimateNumericField, string>;

export type ValidEstimateInputs = {
  weight: number;
  length?: number;
  width?: number;
  height?: number;
};

export type EstimateValidation =
  | { ok: true; values: ValidEstimateInputs; errors: Partial<Record<EstimateNumericField, string>> }
  | { ok: false; values: null; errors: Partial<Record<EstimateNumericField, string>> };

const plainDecimalPattern = /^(?:\d+|\d*\.\d+)$/;

type NumericLimits = { min: number; max: number; decimals: number; unit: string };

function validateFiniteDecimal(
  rawValue: string,
  label: string,
  limits: NumericLimits,
  optional = false,
) {
  const value = rawValue.trim();

  if (!value) return optional ? { value: undefined } : { error: `${label} is required.` };
  if (value.length > 8) return { error: `${label} is too long.` };
  if (!plainDecimalPattern.test(value)) {
    return { error: `Enter ${label.toLowerCase()} as a regular decimal number.` };
  }

  const decimalPlaces = value.includes(".") ? value.length - value.indexOf(".") - 1 : 0;
  if (decimalPlaces > limits.decimals) {
    return { error: `${label} supports up to ${limits.decimals} decimal place.` };
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return { error: `${label} must be a finite number.` };
  if (parsed < limits.min) {
    return { error: `${label} must be at least ${limits.min} ${limits.unit}.` };
  }
  if (parsed > limits.max) {
    return { error: `${label} must be no more than ${limits.max.toLocaleString("en-US")} ${limits.unit}.` };
  }

  return { value: parsed };
}

export function validateEstimateInputs(inputs: EstimateInputs): EstimateValidation {
  const errors: Partial<Record<EstimateNumericField, string>> = {};
  const values = {} as ValidEstimateInputs;
  const fields: Array<[EstimateNumericField, string, NumericLimits, boolean]> = [
    ["weight", "Weight", ESTIMATE_INPUT_LIMITS.weight, false],
    ["length", "Length", ESTIMATE_INPUT_LIMITS.dimension, true],
    ["width", "Width", ESTIMATE_INPUT_LIMITS.dimension, true],
    ["height", "Height", ESTIMATE_INPUT_LIMITS.dimension, true],
  ];

  for (const [field, label, limits, optional] of fields) {
    const result = validateFiniteDecimal(inputs[field], label, limits, optional);
    if ("error" in result) errors[field] = result.error;
    else if (typeof result.value === "number") values[field] = result.value;
  }

  if (Object.keys(errors).length > 0) return { ok: false, values: null, errors };
  return { ok: true, values, errors };
}
